/**
 * Footer language filter — full catalog + "All languages".
 */
import { useEffect, useRef, useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useLanguagePreference } from '../context/LanguagePreferenceContext';
import { getLangCountryCode } from '../config/languages';
import { languageSelectorOptions } from '../lib/languagePreference';

const ALL_LANGUAGES_LABEL = 'All languages';

export default function LanguageSelector() {
  const theme = useTheme();
  const { langFilter, setLangFilter } = useLanguagePreference();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const options = languageSelectorOptions(langFilter);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const select = (lang: string | null) => {
    setLangFilter(lang);
    setOpen(false);
  };

  const label = langFilter ?? ALL_LANGUAGES_LABEL;

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}
    >
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Market language filter"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.375rem',
          padding: '0.375rem 0.5rem',
          fontSize: '0.75rem',
          fontWeight: 500,
          backgroundColor: 'transparent',
          borderRadius: '0.375rem',
          border: `1px solid ${theme.border}`,
          cursor: 'pointer',
          color: theme.footerTextColor ?? theme.textMuted,
        }}
      >
        <span
          className={`fi fi-${langFilter ? getLangCountryCode(langFilter) : 'un'}`}
          style={{ display: 'inline-block', width: '1.25em', height: '0.9em', verticalAlign: 'middle' }}
          aria-hidden
        />
        {label}
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Select market language"
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '0.35rem',
            width: 'fit-content',
            minWidth: '8.5rem',
            maxHeight: '14rem',
            overflowY: 'auto',
            backgroundColor: theme.bgCard,
            borderRadius: '0.375rem',
            border: `1px solid ${theme.border}`,
            boxShadow: '0 12px 36px rgba(0,0,0,0.45)',
            zIndex: 100,
          }}
        >
          <button
            type="button"
            role="option"
            aria-selected={langFilter == null}
            onClick={() => select(null)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              width: '100%',
              padding: '0.5rem 0.75rem',
              fontSize: '0.8125rem',
              fontWeight: 500,
              border: 'none',
              backgroundColor: langFilter == null ? theme.bg : 'transparent',
              color: theme.textPrimary,
              cursor: 'pointer',
              textAlign: 'left',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              className="fi fi-un"
              style={{ display: 'inline-block', width: '1.25em', height: '0.9em', verticalAlign: 'middle' }}
              aria-hidden
            />
            {ALL_LANGUAGES_LABEL}
          </button>
          {options.map((lang) => (
            <button
              key={lang}
              type="button"
              role="option"
              aria-selected={langFilter === lang}
              onClick={() => select(lang)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.375rem',
                width: '100%',
                padding: '0.5rem 0.75rem',
                fontSize: '0.8125rem',
                fontWeight: 500,
                border: 'none',
                backgroundColor: langFilter === lang ? theme.bg : 'transparent',
                color: theme.textPrimary,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span
                className={`fi fi-${getLangCountryCode(lang)}`}
                style={{ display: 'inline-block', width: '1.25em', height: '0.9em', verticalAlign: 'middle' }}
                aria-hidden
              />
              {lang}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
